var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Module } from "../module";
import * as THREE from "three";
import { CSG } from "three-csg-ts";
import { ModuleId } from "flavr-common";
import { BooleanMeshIntersectionRequestMessage, BooleanMeshIntersectionResponseMessage } from "../../messages/booleanMeshOperation/BooleanMeshIntersectionMessages";
import { RequestObject3DAnswerMessage, RequestObject3DMessage } from "../../messages/rendering/MeshMessages";
import { Mesh } from "three";
import { ConvexGeometry } from "three/examples/jsm/geometries/ConvexGeometry";
/**
 * Provides boolean mesh intersection queries for modules that need geometric overlap checks without duplicating CSG setup.
 *
 * Messages:
 * - `BooleanMeshIntersectionRequestMessage` - Requests an overlap test between two model meshes.
 * - `RequestObject3DMessage` - Is used internally to fetch the scene objects that should be tested.
 * - `BooleanMeshIntersectionResponseMessage` - Returns whether the two models intersect.
 */
export class BooleanMeshOperationModule extends Module {
    constructor() {
        super(ModuleId.BOOLEAN_MESH_OPERATION);
        this.onBooleanMeshIntersectionRequest = (msg, askId) => __awaiter(this, void 0, void 0, function* () {
            // This function calculating the intersection of two meshes.
            // The first mesh is used as mesh and the second mesh is used as convex hull.
            let intersects = false;
            const answerModelA = yield this.connection.askByMessageType(new RequestObject3DMessage(msg.modelUIDA), RequestObject3DAnswerMessage);
            const answerModelB = yield this.connection.askByMessageType(new RequestObject3DMessage(msg.modelUIDB), RequestObject3DAnswerMessage);
            if (answerModelA.object instanceof Mesh && answerModelB.object instanceof Mesh) {
                const cloneA = answerModelA.object.clone();
                cloneA.geometry = answerModelA.object.geometry.clone();
                const cloneB = answerModelB.object.clone();
                cloneB.geometry = answerModelB.object.geometry.clone();
                cloneA.geometry.applyMatrix4(cloneA.matrixWorld);
                cloneB.geometry.applyMatrix4(cloneB.matrixWorld);
                const position = cloneB.geometry.attributes.position.array;
                const points = [];
                for (let i = 0; i < position.length; i += 3) {
                    points.push(new THREE.Vector3(position[i], position[i + 1], position[i + 2]));
                }
                const convexGeometry = new ConvexGeometry(points);
                const convexHull = new THREE.Mesh(convexGeometry, new THREE.MeshBasicMaterial());
                const intersection = this.intersect(cloneA, convexHull);
                if (intersection) {
                    if (intersection.geometry) {
                        if (intersection.geometry.attributes.position.array.length > 0) {
                            intersects = true;
                        }
                    }
                }
            }
            this.connection.answer(askId, new BooleanMeshIntersectionResponseMessage(intersects));
        });
    }
    /**
     * Computes the CSG intersection of two meshes. The first mesh is treated as the primary source mesh and the second mesh supplies the convex comparison volume.
     */
    intersect(mesh1, mesh2) {
        return CSG.intersect(mesh1, mesh2);
    }
    /**
     * Registers the ask/answer handler that serves boolean mesh intersection requests from other modules.
     */
    init() {
        this.connection.subscribeForAskRequest(BooleanMeshIntersectionRequestMessage, this.onBooleanMeshIntersectionRequest);
    }
}
